import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BlueButton, useOpenFailedInstalmentPayment } from '@share/components';
import { walletDrawerActions } from '@share/store/slices';
import { FunctionalRootState, hasPersistedMemberSession } from '@share/utils';

import { FAILED_INSTALMENT_POPUP_DISMISSED_SESSION_KEY } from './constants';

import styles from './styles.module.scss';

function isPopupDismissedForSession(): boolean {
  try {
    return sessionStorage.getItem(FAILED_INSTALMENT_POPUP_DISMISSED_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

function markPopupDismissedForSession(): void {
  try {
    sessionStorage.setItem(FAILED_INSTALMENT_POPUP_DISMISSED_SESSION_KEY, 'true');
  } catch {
    // Ignore storage failures — popup remains closable for the current render cycle.
  }
}

export function FailedInstalmentPaymentModal(): React.ReactElement | null {
  const dispatch = useDispatch();
  const { user, account } = useSelector((state: FunctionalRootState) => state.loginStore);
  const isMemberLoggedIn = hasPersistedMemberSession(account);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { hasOpenFailedInstalmentPayment, isChecking } = useOpenFailedInstalmentPayment({
    email: user?.email,
    organizationId: user?.userAdditionalData?.organizationId,
    enabled: isMemberLoggedIn && Boolean(user?.email),
  });

  useEffect(() => {
    if (isChecking || !hasOpenFailedInstalmentPayment || isPopupDismissedForSession()) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
  }, [hasOpenFailedInstalmentPayment, isChecking]);

  const handleClose = useCallback((): void => {
    markPopupDismissedForSession();
    setIsOpen(false);
  }, []);

  const handleOpenWallet = useCallback((): void => {
    markPopupDismissedForSession();
    setIsOpen(false);
    dispatch(walletDrawerActions.setWalletDrawerOpen(true));
  }, [dispatch]);

  const footer = useMemo(
    () => (
      <div className={styles.footer}>
        <BlueButton className={styles.primaryAction} onClick={handleOpenWallet}>
          <FormattedMessage id="certificates.wallet.failedInstalmentModal.openWallet" />
        </BlueButton>
      </div>
    ),
    [handleOpenWallet],
  );

  if (!isMemberLoggedIn) {
    return null;
  }

  return (
    <Modal
      className={styles.modal}
      open={isOpen}
      title={null}
      onCancel={handleClose}
      footer={footer}
      closable
      centered
      width={480}
      destroyOnClose
    >
      <p className={styles.message}>
        <FormattedMessage id="certificates.wallet.failedInstalmentModal.message" />
      </p>
    </Modal>
  );
}
